import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import axios from 'axios';
import { toast } from 'react-toastify';
import authService from './api-authorization/AuthorizeService';
import Select, { ActionMeta, MultiValue } from 'react-select'

interface ReportJobKpis {
    loading: boolean;
    startDate: string;
    endDate: string;
    truckId: number;
    teamLeadId: number;
    teamMemberId: number;
    itemId: number;
    scheduleBranchId: number;
    includeRolloverJobs: boolean;   //jobs over multiple days
    includeAssistedJobs: boolean;   //jobs over multiple trucks
    truckOptions: GenericList[];
    teamOptions: OptionsItem[];
    teamFilter: OptionsItem[];
    scheduleBranchOptions: GenericList[];
    itemOptions: OptionsItem[];
    itemFilter: OptionsItem[];
    errors: any;
}

interface GenericList {
    id: number;
    name: string;
}

type OptionsItem = {
    label: string,
    value: number
}

export class ReportJobKpisData extends React.Component<RouteComponentProps<{}>, ReportJobKpis> {
    constructor(props: any) {
        super(props);

        this.state = {
            loading: true,
            startDate: "",
            endDate: "",
            truckId: -1,
            teamLeadId: -1,
            teamMemberId: -1,
            itemId: -1,
            scheduleBranchId: -1,
            includeRolloverJobs: true,
            includeAssistedJobs: true,
            truckOptions: [],
            teamOptions: [],
            teamFilter: [],
            itemOptions: [],
            itemFilter: [],
            scheduleBranchOptions: [],
            errors: {}
        } 
    }

    componentDidMount() {
        this.getData();
    }

    async getData() {
        const token = await authService.getAccessToken();
        const user = await authService.getUser();

        axios.get('api/User/CheckUserPermission?Permission=RunReports&SubId=' + user.sub, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            if (res.data == true) {
                //get dropdowns
                axios.get('api/Report/GetJobKpiDropdowns?SubId=' + user.sub, {
                    headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
                })
                .then(res => {
                    this.setState({ scheduleBranchOptions: res.data.scheduleBranchOptions, scheduleBranchId: res.data.scheduleBranchId, truckOptions: res.data.truckOptions, teamOptions: res.data.teamOptions, itemOptions: res.data.itemOptions, loading: false });
                })
                .catch(error => {
                    toast.error(error.message);
                });
            } else if (res.data == false) {
                //redirect to 403 permission denied
                this.props.history.push("/accessdenied")
            }
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    async getTeamByBranch(schedBranchId: number) {
        const token = await authService.getAccessToken();

        axios.get('api/User/GetUsersWithTruckRole?CompanyId=2&ScheduleBranchId=' + schedBranchId, {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` }
        })
        .then(res => {
            this.setState({ teamOptions: res.data });
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    handleBranchChange = (e: any) => {
        let schedBranchId = e.target.value * 1;
        this.setState({ scheduleBranchId: schedBranchId, errors: "" });
        if (schedBranchId > 0) {
            this.getTeamByBranch(schedBranchId);
        }
    }

    handleChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.value;
        change["errors"] = "";
        this.setState(change);
    }

    handleCheckboxChange = (e: any) => {
        let change: any = {};
        change[e.target.name] = e.target.checked;
        this.setState(change);
    }

    handleMultiTeamChange = (selected: MultiValue<OptionsItem>, actionMeta: ActionMeta<OptionsItem>) => {
        var result = [...selected];
        this.setState({ teamFilter: result });
    }

    handleMultiItemChange = (selected: MultiValue<OptionsItem>, actionMeta: ActionMeta<OptionsItem>) => {
        var result = [...selected];
        this.setState({ itemFilter: result });
    }

    validate = () => {
        let fields = this.state;
        let errors: any = {};
        let formIsValid = true;

        if (!fields.startDate) {
            formIsValid = false;
            errors["startDate"] = "Start Date is required";
        }

        if (!fields.endDate) {
            formIsValid = false;
            errors["endDate"] = "End Date is required";
        }

        if (fields.startDate && fields.endDate && fields.endDate < fields.startDate) {
            formIsValid = false;
            errors["startDate"] = "Start Date must be < End Date";
        }

        if (fields.scheduleBranchId < 0) {
            formIsValid = false;
            errors["scheduleBranchId"] = "Branch is required";
        }

        this.setState({ errors: errors });
        return formIsValid;
    }

    runReport = (e: any) => {
        e.preventDefault();
        //run the report and export the data

        if (this.validate()) {
            this.runRpt();
        }
    }

    async runRpt() {
        var reportCriteria = {
            startDate: this.state.startDate,
            endDate: this.state.endDate,
            scheduleBranchId: this.state.scheduleBranchId,
            truckId: this.state.truckId,
            team: this.state.teamFilter,
            items: this.state.itemFilter,
            incRolloverJobs: this.state.includeRolloverJobs,
            incAssistedJobs: this.state.includeAssistedJobs
        };

        const token = await authService.getAccessToken();

        axios('api/Report/RunJobKpiReport', {
            headers: !token ? {} : { 'Authorization': `Bearer ${token}` },
            method: 'POST',
            data: reportCriteria,
            responseType: 'blob' //Force to receive data in a Blob Format
        })
        .then(res => {
            //DOWNLOAD FILE TO BROWSER
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Job KPIs.xlsx');
            document.body.appendChild(link);
            link.click();
        })
        .catch(error => {
            toast.error(error.message);
        });
    }

    render() {
        let contents = this.state.loading
            ? <p><em>Loading...</em></p>
            : this.renderDetails();


        return <div>
            {contents}
        </div>;
    }

    renderDetails = () => {
        let scheduleBranchOptions = this.state.scheduleBranchOptions;
        let truckOptions = this.state.truckOptions;
        let teamOptions = this.state.teamOptions;
        let itemOptions = this.state.itemOptions;

        return (
            <div className="kpireport__screen--height">
                <div>
                    <h1>Job KPI Report</h1>
                </div>
                <div className="marginTop20">
                    <span>Select a Start Date and End Date to run the report. Use the other options for filtering the report.</span>
                </div>
                <div>
                    <div className="input-group-parent">
                        <label className="input-group" htmlFor="startDate">
                            <span className="label">Start Date</span>
                            <input type='date' id="startDate" name="startDate" className='input' value={this.state.startDate} onChange={(e) => this.handleChange(e)}></input>
                        </label>
                        <span className={this.state.errors["startDate"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["startDate"]}</span>

                        <label className="input-group" htmlFor="endDate">
                            <span className="label">End Date</span>
                            <input type='date' id="endDate" name="endDate" className='input' value={this.state.endDate} onChange={(e) => this.handleChange(e)}></input>
                        </label>
                        <span className={this.state.errors["endDate"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["endDate"]}</span>

                        <label className="input-group" htmlFor="scheduleBranchId">
                            <span className="label">Branch</span>
                            <select className="select" id="scheduleBranchId" name="scheduleBranchId" value={this.state.scheduleBranchId} onChange={(e) => this.handleBranchChange(e)}>
                                {scheduleBranchOptions.map(branch =>
                                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                                )};
                            </select>
                        </label>
                        <span className={this.state.errors["scheduleBranchId"] ? "label errors marginLeftLabel" : "hidden"}>{this.state.errors["scheduleBranchId"]}</span>

                        <div className="marginLeftLabel">
                            <label className="input-group--inline job__checkboxPosition kpireport__checkbox--left" htmlFor="includeRolloverJobs">
                                <span className="label">Include Rollover Jobs</span>
                                <div>
                                    <input type="checkbox" className="kpireport__checkbox--margin" name="includeRolloverJobs" id="includeRolloverJobs" checked={this.state.includeRolloverJobs} onChange={(e) => this.handleCheckboxChange(e)}></input>
                                </div>
                            </label>
                            <label className="input-group--inline job__checkboxPosition" htmlFor="includeAssistedJobs">
                                <span className="label">Include Assisted Jobs</span>
                                <div>
                                    <input type="checkbox" className="kpireport__checkbox--margin" name="includeAssistedJobs" id="includeAssistedJobs" checked={this.state.includeAssistedJobs} onChange={(e) => this.handleCheckboxChange(e)}></input>
                                </div>
                            </label>
                        </div>
                    </div>
                    <div className="input-group-parent">                  
                        <label className="input-group" htmlFor="truckId">
                            <span className="label">Truck</span>
                            <select className="select" id="truckId" name="truckId" value={this.state.truckId} onChange={(e) => this.handleChange(e)}>
                                <option defaultValue="-1" value="-1"></option>
                                {truckOptions.map(truck =>
                                    <option key={truck.id} value={truck.id}>{truck.name}</option>
                                )};
                            </select>
                        </label>
                        <label className="input-group" htmlFor="teamMemberId">
                            <span className="label">Team</span>
                            <Select isMulti id="teamMemberFilter" name="teamMemberFilter" options={teamOptions} onChange={this.handleMultiTeamChange} />
                        </label>
                        <label className="input-group" htmlFor="itemId">
                            <span className="label">Item/Product</span>
                            <Select isMulti id="itemFilter" name="itemFilter" options={itemOptions} onChange={this.handleMultiItemChange} />
                        </label> 
                    </div>
                    
                </div>
                <button className="defaultbutton marginLeftLabel" type='button' onClick={this.runReport}>Run Report</button>
            </div>
        );

    }

};